<template>
  <div>
    <div :id="triggerId" class="inline-block">
      <slot name="popoverReference"></slot>
    </div>
    <IonPopover
      ref="popoverRef"
      :trigger="effectiveTrigger"
      trigger-action="click"
      :css-class="`${cssClass ?? ''} custom-ion-popover`"
      :side="side"
      :alignment="alignment"
      :size="size"
      @willPresent="onWillPresent"
      @didDismiss="onDidDismiss"
    >
      <div class="p-1">
        <slot name="popoverContent" :dismissPopover="dismissPopover"></slot>
      </div>
    </IonPopover>
  </div>
</template>

<script lang="ts">
import { IonPopover } from "@ionic/vue";
import type { PositionAlign, PositionSide } from "@ionic/core";
import { computed, defineComponent, onMounted, PropType, ref } from "vue";

let triggerIdCounter = 0;

export default defineComponent({
  name: "CustomIonPopover",
  components: { IonPopover },
  props: {
    cssClass: { type: String, default: undefined },
    disabled: { type: Boolean, default: false },
    side: { type: String as PropType<PositionSide>, default: "bottom" },
    alignment: { type: String as PropType<PositionAlign>, default: "center" },
    size: { type: String as PropType<"cover" | "auto">, default: "auto" },
  },
  emits: ["onOpen", "onClose"],
  setup(props, { emit }) {
    const triggerId = `custom-ion-popover-trigger-${++triggerIdCounter}`;
    const popoverRef = ref<{ $el: HTMLElement & { dismiss?: () => Promise<unknown> } } | null>(
      null,
    );
    const isMounted = ref(false);

    const effectiveTrigger = computed(() =>
      props.disabled || !isMounted.value ? undefined : triggerId,
    );

    onMounted(() => {
      isMounted.value = true;
    });

    function onWillPresent(): void {
      emit("onOpen");
    }

    function onDidDismiss(): void {
      emit("onClose");
    }

    function dismissPopover(): void {
      popoverRef.value?.$el?.dismiss?.().catch(() => {});
    }

    return {
      triggerId,
      effectiveTrigger,
      popoverRef,
      onWillPresent,
      onDidDismiss,
      dismissPopover,
    };
  },
});
</script>
<style lang="scss">
.custom-ion-popover {
  --width: auto;
}

ion-popover.tooltip-style {
  --background: var(--color-light);
  --box-shadow: var(--shadow-card);
  --backdrop-opacity: 0;
  --width: auto;
  --max-width: 16rem;
  &::part(arrow) {
    display: none;
  }
  &::part(content) {
    border: 1px solid var(--color-medium-light);
    border-radius: var(--control-radius);
  }
}
</style>
